import solutionVisualImg from "../../image/education/education-solution-visual.png";
import "./educationSolutionVisual.css";
import { DataKeys } from "../../types";
import { useLang } from "../Context";
import { SlideInAnim, StaggerInView, StaggerInViewItem } from "../tools/Animations";
import { HealthSection } from "../health/HealthSection";
import { HealthSectionHeader } from "../health/HealthSectionHeader";
import { getLocalized, HealthLang } from "../health/healthTypes";
import {
    healthBody,
    healthCardTitle,
    healthSolutionCapabilityAccent,
} from "../health/healthVisualStyles";

interface EducationSolutionSectionProps {
    data: DataKeys;
}

const capabilityIcons = [
    <svg key="virtual" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="8" r="3.2" />
        <path d="M5.8 18.2c1.8-2.4 4-3.6 6.2-3.6 2.2 0 4.4 1.2 6.2 3.6" />
    </svg>,
    <svg key="coach" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 6h16" />
        <path d="M4 10h12" />
        <path d="M4 14h10" />
        <path d="M4 18h8" />
    </svg>,
    <svg key="cases" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <path d="M14 2v6h6" />
        <path d="M8 13h8" />
    </svg>,
    <svg key="analytics" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 19V5" />
        <path d="M4 19h16" />
        <path d="M8 17V9" />
        <path d="M12 17V7" />
        <path d="M16 17v-4" />
    </svg>,
] as const;

const educationSolutionCapabilityCard =
    "flex h-full w-full min-w-0 flex-col items-start rounded-xl border border-grey-100/70 bg-[#fcfefe] px-4 py-3.5 text-left shadow-[0_4px_14px_rgba(16,24,40,0.04)] [text-align:left] lg:px-3.5 lg:py-3";

const educationSolutionCapabilityIconWrap =
    "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-50/65 text-[#037C84]";

export function EducationSolutionSection({ data }: EducationSolutionSectionProps) {
    const { lang } = useLang();
    const hLang = lang as HealthLang;
    const pillars: DataKeys[] = data?.pillars ?? [];
    const sectionId = "solution";
    const visualAlt = getLocalized(data?.visualAlt, hLang);

    return (
        <HealthSection id={sectionId} ariaLabelledBy={`${sectionId}-title`} compact>
            <div className="grid w-full min-w-0 grid-cols-1 gap-8 lg:grid-cols-[minmax(0,0.52fr)_minmax(0,0.48fr)] lg:items-center lg:gap-10">
                <HealthSectionHeader
                    eyebrow={getLocalized(data?.pillarsEyebrow, hLang) || "Sensei capabilities"}
                    title={getLocalized(data?.title, hLang)}
                    titleId={`${sectionId}-title`}
                    subtitle={getLocalized(data?.intro, hLang)}
                    subtitleWide
                    className="mb-0 lg:mb-0"
                />
                <SlideInAnim direction="right">
                    <div className="education-solution-visual">
                        <img
                            src={solutionVisualImg}
                            alt={visualAlt}
                            className="education-solution-visual__img"
                            loading="lazy"
                        />
                        <div className="education-solution-visual__fade" aria-hidden="true" />
                    </div>
                </SlideInAnim>
            </div>
            <StaggerInView className="mt-6 grid w-full min-w-0 grid-cols-1 items-stretch gap-3 sm:grid-cols-2 lg:mt-8 lg:grid-cols-4 lg:gap-3">
                {pillars.map((pillar, index) => (
                    <StaggerInViewItem key={(pillar.id as string) ?? index} direction="bottom">
                        <article className={`${educationSolutionCapabilityCard} flex flex-col`}>
                            <div className="mb-2 flex w-full items-start justify-between gap-2">
                                <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-teal-500">
                                    {String(index + 1).padStart(2, "0")}
                                </span>
                                <span className={educationSolutionCapabilityIconWrap} aria-hidden="true">
                                    {capabilityIcons[index]}
                                </span>
                            </div>
                            <h3 className={`${healthCardTitle} text-sm font-semibold leading-snug text-grey-900 [text-align:left]`}>
                                {getLocalized(pillar.title, hLang)}
                            </h3>
                            <span className={`${healthSolutionCapabilityAccent} mt-1.5`} aria-hidden="true" />
                            <p className={`${healthBody} mt-1.5 text-xs leading-relaxed text-grey-600`}>
                                {getLocalized(pillar.text, hLang)}
                            </p>
                        </article>
                    </StaggerInViewItem>
                ))}
            </StaggerInView>
        </HealthSection>
    );
}
